import { animate, style, transition, trigger } from '@angular/animations';
import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { take } from 'rxjs/operators';

import { ROUTING_KEY } from '@app/core/enums/routing-key.enum';
import { PassportProgressHelper } from '@app/core/helpers/passport-progress.helper';
import {
  BtrEventModel,
  BtrPassportModel,
  ClusterModel,
  EventView,
  PlaceModel,
  ProfileModel,
} from '@app/core/models';
import {
  AnalyticsEvents,
  AnalyticsService,
  APP_CONFIG,
  EventsService,
  IAppConfig,
  StorageService,
} from '@app/core/services';
import { BtCostPercentageService } from '@app/core/services/bt-cost-percentage.service';

@Component({
  selector: 'app-event-card',
  templateUrl: './event-card.component.html',
  styleUrls: ['./event-card.component.scss'],
  animations: [
    trigger('contentIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('500ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('500ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class EventCardComponent implements OnInit {
  @Input() public event: BtrEventModel | BtrPassportModel;
  @Input() public place: PlaceModel;
  @Input() public itemWidth: number;
  @Input() public forPopup = false;
  @Input() public forBeaconPage = false;
  @Input() public forTicketList = false;
  @Input() public fromEventList = false;
  @Input() public forStandalone = false;
  @Input() public forDestinationPage = false;

  public places: Array<PlaceModel> = [];
  public profile: ProfileModel;
  public displaySuccess = false;
  public canPurchase = false;
  public isFree = false;
  public ticketMinPrice: number;
  public standalone: boolean;
  public isPassportMode: boolean = false;
  public readonly EventView = EventView;

  public readonly isBrowser: boolean;

  constructor(
    @Inject(APP_CONFIG) private readonly appConfig: IAppConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private analyticsService: AnalyticsService,
    private eventsService: EventsService,
    private btCostPercentageService: BtCostPercentageService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.route.url.pipe(take(1)).subscribe(() => {
      this.standalone =
        this.route.snapshot.data.page === ROUTING_KEY.STANDALONE_EVENT ||
        this.route.snapshot.data.page === ROUTING_KEY.STANDALONE_PASSPORT;
      this.isPassportMode =
        this.event.isPassport ||
        this.route.snapshot.data.page === ROUTING_KEY.STANDALONE_PASSPORT ||
        this.route.snapshot.data.page === ROUTING_KEY.PASSPORT;
    });
    this.profile = this.storageService.PROFILE;
    this.initScreen();
  }

  get eventLink(): Array<string | number> | null {
    if (!this.event || this.forStandalone) {
      return null;
    }

    if (this.appConfig.settings.is_white_label) {
      return [
        this.event.isPassport ? '/passports' : '/events',
        this.event.slug,
      ];
    }

    const selectedCluster: ClusterModel = this.storageService.SELECTED_CLUSTER;
    if (!selectedCluster?.slug) {
      return [
        this.event.isPassport ? '/s/passport' : '/s/event',
        this.event.slug,
      ];
    }

    return [
      this.event.isPassport ? '/passports' : '/events',
      selectedCluster.slug,
      this.event.slug,
    ];
  }

  get eventHref(): string | null {
    return this.eventLink
      ? this.router.serializeUrl(this.router.createUrlTree(this.eventLink))
      : null;
  }

  get passportPlacesToVisitRequiredCount(): number {
    return PassportProgressHelper.getCompletionThreshold(
      this.event,
      this.event?.tickets?.[0],
    );
  }

  get hasEventLocations(): boolean {
    return (
      (this.places?.length ?? 0) > 0 || (this.event?.locations?.length ?? 0) > 0
    );
  }

  onEventLinkClick(clickEvent: MouseEvent): void {
    clickEvent.stopPropagation();
    if (
      clickEvent.button !== 0 ||
      clickEvent.ctrlKey ||
      clickEvent.metaKey ||
      clickEvent.shiftKey ||
      clickEvent.altKey
    ) {
      return;
    }

    clickEvent.preventDefault();
    this.showEventDetails();
  }

  showEventDetails(): void {
    if (this.forStandalone) {
      return;
    }
    const parameters: {
      eventId: string;
      clusterId?: number;
      fromPlaceId?: number;
    } = {
      eventId: this.event.id,
    };

    const path = this.eventLink;
    if (!path) {
      return;
    }

    if (this.appConfig.settings.is_white_label) {
      parameters.clusterId = this.appConfig.settings.cluster;
    } else {
      const selectedCluster: ClusterModel =
        this.storageService.SELECTED_CLUSTER;
      parameters.clusterId = selectedCluster?.id;
    }
    if (this.place) {
      parameters.fromPlaceId = this.place.id;
    }

    this.analyticsService.emitEvent(AnalyticsEvents.eventView, parameters);
    if (this.forTicketList && this.isBrowser) {
      window.open(path?.join('/'), '_blank');
      return;
    }
    this.eventsService.setIsLoading(true);
    switch (
      !this.fromEventList &&
      !this.forDestinationPage &&
      this.route.snapshot.pathFromRoot.length < 4
    ) {
      case true:
        this.router.navigate(
          this.event.isPassport
            ? path.map(item => (item !== '/passports' ? item : 'passport'))
            : path.map(item => (item !== '/events' ? item : 'event')),
          { relativeTo: this.route },
        );
        break;
      default:
        this.router.navigate(path);
        break;
    }
  }

  getImage(): any {
    if (this.event?.image) {
      return {
        background: `url(${this.event.image
          .replace('original', 'thumbnail_600x300')
          .replace('.png', '.jpg')}) no-repeat center`,
        'background-size': 'cover',
      };
    }

    return {
      background: `url("/assets/img/no-photo.svg") no-repeat center`,
      'background-size': 'cover',
    };
  }

  private initScreen(): void {
    if (this.place) {
      this.places = [this.place];
    } else {
      this.places = Array.isArray(this.event._places)
        ? this.event._places.map(item => new PlaceModel(item))
        : [];
    }

    if (this.event._tickets && this.event._tickets.length > 0) {
      for (const ticket of this.event._tickets) {
        if (
          new Date(ticket.start).getTime() < Date.now() &&
          (ticket?.end?.length
            ? new Date(ticket.end).getTime() > Date.now()
            : true)
        ) {
          if (!this.isFree && ticket.cost === 0) {
            this.isFree = true;
          }
          const cost = ticket.is_fee_on_consumer
            ? +this.btCostPercentageService.calcBusinessProfitWithConsumerFee(
                this.event,
                ticket.cost,
              ) * 100
            : ticket.cost;
          if (
            cost > 0 &&
            (cost < this.ticketMinPrice || !this.ticketMinPrice)
          ) {
            this.ticketMinPrice = cost;
          }
        }
      }
      // const d1 = DateTime.local();
      // const d2 = this.event.end ? DateTime.fromISO(this.event.end) : DateTime.fromISO(this.event.start);
      // this.canPurchase = d1 < d2;
      if (!this.profile) {
        this.canPurchase = true;
        this.displaySuccess = false;
      } else if (
        this.event._tickets[0].statuses &&
        this.event._tickets[0].statuses.length > 0
      ) {
        for (const status of this.event._tickets[0].statuses) {
          if (
            status.status === 'purchased' ||
            status.status === 'granted' ||
            status.status === 'verified'
          ) {
            this.canPurchase = false;
            this.displaySuccess = true;
          }
        }
      } else {
        this.canPurchase = true;
        this.displaySuccess = false;
      }
    }
  }
}
